import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { LoadingSpinner, EmptyState } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaSitemap } from 'react-icons/fa';
import { MdArrowBack, MdWarningAmber } from 'react-icons/md';
import { toast } from 'react-toastify';

import { getOrganizationEmployeeChart } from 'redux/organization/employeeSlice';
import { getAllSites } from 'redux/organization/siteSlice';
import { getAllDepartments } from 'redux/organization/departmentSlice';

import ChangeManagerForm from 'components/app/organization/employee/ChangeManagerForm';
import OrgChartToolbar from './OrgChartToolbar';
import OrgChartCanvas from './OrgChartCanvas';
import OrgChartUnassigned from './OrgChartUnassigned';
import OrgChartDetailPanel from './OrgChartDetailPanel';
import OrgChartEditDrawer from './OrgChartEditDrawer';
import useNodeDrag from './useNodeDrag';
import useOrgChartData from './useOrgChartData';
import useOrgChartLayout from './useOrgChartLayout';
import usePanZoom from './usePanZoom';
import { exportChartPng, exportChartPdf } from './orgChartExport';
import {
  COMPANY_ROOT_ID, DEFAULT_OPEN_DEPTH, NODE_WIDTH, NODE_HEIGHT, ancestorIdsOf,
} from './orgChartHelpers';

const EMPTY_FILTERS = { siteIds: [], departmentIds: [], status: '' };
// A shared frozen array, not a fresh `|| []` per render: an inline literal gives
// a new identity every time and defeats every useMemo downstream of it.
const EMPTY_LIST = Object.freeze([]);

const EmployeeOrgChartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: employees, loading, loadedAt, status: loadedStatus } = useSelector(
    (state) => state.organization_employee.orgChart,
  );
  const siteData = useSelector((state) => state.organization_site.allData?.data) || EMPTY_LIST;
  const departmentData = useSelector((state) => state.organization_department.allData?.data) || EMPTY_LIST;

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [focusedId, setFocusedId] = useState(null);
  const [matchIndex, setMatchIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [managerTargets, setManagerTargets] = useState(null);
  const [editing, setEditing] = useState(null);
  const [initialLayoutReady, setInitialLayoutReady] = useState(false);
  // Employment status is a SERVER filter: deactivated employees are not in the
  // payload, so changing it refetches rather than filtering what is already here.
  const [employmentStatus, setEmploymentStatus] = useState('active');

  const viewportRef = useRef(null);
  const captureRef = useRef(null);
  const searchRef = useRef(null);
  const seededRef = useRef(false);
  const pendingFocusRef = useRef(searchParams.get('focus'));

  useEffect(() => {
    if (!loadedAt) dispatch(getOrganizationEmployeeChart(employmentStatus));
    if (!siteData.length) dispatch(getAllSites());
    if (!departmentData.length) dispatch(getAllDepartments());
    // Fetch-once on mount; the deps are intentionally the cache markers only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    tree, unassigned, inactiveUnassigned, matchedList, pathToMatches,
    childrenByParent, parentById, byId, anomalies,
  } = useOrgChartData({ employees, filters, query, collapsedIds });

  const { nodes, edges, width, height } = useOrgChartLayout(tree);

  const {
    transform, isPanning, zoomIn, zoomOut, fitToScreen, centerOn, panHandlers, wasDragged,
  } = usePanZoom({
    viewportRef,
    contentSize: { width, height },
  });

  const nodePositions = useMemo(
    () => new Map(nodes.map((node) => [node.id, { x: node.x, y: node.y }])),
    [nodes],
  );

  /** Everyone with reports, i.e. every node that has something to collapse. */
  const collapsibleIds = useMemo(() => {
    const ids = [COMPANY_ROOT_ID];
    childrenByParent.forEach((children, parentId) => {
      if (children.length > 0 && parentId !== COMPANY_ROOT_ID) ids.push(parentId);
    });
    return ids;
  }, [childrenByParent]);

  /**
   * Seed the collapsed set once the data lands: everything below
   * DEFAULT_OPEN_DEPTH starts closed, so the first paint shows the shape of the
   * company rather than 670 cards at once.
   */
  useEffect(() => {
    if (seededRef.current || !employees.length) return;
    seededRef.current = true;

    const depthOf = (id) => ancestorIdsOf(id, parentById).length + 1;
    setCollapsedIds(new Set(collapsibleIds.filter((id) => id !== COMPANY_ROOT_ID && depthOf(id) >= DEFAULT_OPEN_DEPTH)));
    // Fit only after this collapsed layout has rendered. Fitting the initial
    // 500+ node tree first leaves the later compact tree off-screen at min zoom.
    setInitialLayoutReady(true);
  }, [employees.length, collapsibleIds, parentById]);

  const openPathTo = useCallback((id) => {
    const path = ancestorIdsOf(id, parentById);
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      path.forEach((ancestorId) => next.delete(ancestorId));
      next.delete(COMPANY_ROOT_ID);
      return next;
    });
  }, [parentById]);

  const focusOn = useCallback((id) => {
    const position = nodePositions.get(id);
    if (!position) return false;

    setFocusedId(id);
    centerOn(position.x + NODE_WIDTH / 2, position.y + NODE_HEIGHT / 2);
    return true;
  }, [nodePositions, centerOn]);

  const selectNode = useCallback((id) => {
    setSelectedId(id);
    openPathTo(id);
    focusOn(id);
  }, [openPathTo, focusOn]);

  /**
   * Everyone under a node, so a drop onto one's own descendant can be refused
   * before it reaches the server. The backend rejects loops too — this just
   * means the card never lights up green for an impossible move.
   */
  const descendantsOf = useCallback((rootId) => {
    const found = new Set();
    const stack = [rootId];

    while (stack.length) {
      const current = stack.pop();
      (childrenByParent.get(current) || []).forEach((childId) => {
        if (found.has(childId)) return;
        found.add(childId);
        stack.push(childId);
      });
    }

    return found;
  }, [childrenByParent]);

  const isValidDropTarget = useCallback((draggedNode, targetId) => {
    if (!draggedNode || targetId === draggedNode.id) return false;
    if (parentById.get(draggedNode.id) === targetId) return false; // already there
    return !descendantsOf(draggedNode.id).has(targetId);
  }, [parentById, descendantsOf]);

  const refetchChart = useCallback(() => {
    dispatch(getOrganizationEmployeeChart(employmentStatus));
  }, [dispatch, employmentStatus]);

  useEffect(() => {
    if (loadedAt && loadedStatus !== employmentStatus) {
      dispatch(getOrganizationEmployeeChart(employmentStatus));
    }
  }, [dispatch, employmentStatus, loadedStatus, loadedAt]);

  const handleDrop = useCallback((draggedNode, targetId) => {
    const target = byId.get(targetId);
    if (!target) return;

    // A drag is easy to slip: confirm by name before anything is written.
    setManagerTargets({ people: [draggedNode], suggested: target });
  }, [byId]);

  const {
    dragging, dropTargetId, ghostPosition, handlersFor: dragHandlersFor, consumedClick,
  } = useNodeDrag({ enabled: true, onDrop: handleDrop, isValidTarget: isValidDropTarget });

  /** Deep link from the employee detail screen: ?focus=<id>. */
  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending || !employees.length) return;

    const id = Number(pending);
    if (!byId.has(id)) {
      pendingFocusRef.current = null;
      toast.info('That employee is not on the chart — they may be deactivated.');
      return;
    }

    openPathTo(id);
    // The node only gets a position after the branch above it is open, so retry
    // on the next layout rather than centring on a node that is not laid out yet.
    if (focusOn(id)) {
      pendingFocusRef.current = null;
      searchParams.delete('focus');
      setSearchParams(searchParams, { replace: true });
    }
  }, [employees.length, byId, openPathTo, focusOn, searchParams, setSearchParams]);

  /** A search hit deep in a collapsed branch is useless unless the path opens. */
  useEffect(() => {
    if (pathToMatches.size === 0) return;

    setCollapsedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      pathToMatches.forEach((id) => {
        if (next.delete(id)) changed = true;
      });
      return changed ? next : prev;
    });
  }, [pathToMatches]);

  useEffect(() => setMatchIndex(0), [query]);

  const goToMatch = useCallback((index) => {
    if (!matchedList.length) return;

    const bounded = (index + matchedList.length) % matchedList.length;
    setMatchIndex(bounded);
    focusOn(matchedList[bounded].id);
  }, [matchedList, focusOn]);

  // Centre the first hit as soon as the branch containing it has opened.
  useEffect(() => {
    if (matchedList.length) focusOn(matchedList[Math.min(matchIndex, matchedList.length - 1)].id);
    // recentre only on new results/layout; matchIndex moves are centred by goToMatch, and re-running on them would yank the viewport
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedList.length, nodePositions]);

  const toggleNode = useCallback((id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(
    () => setCollapsedIds(new Set(collapsibleIds.filter((id) => id !== COMPANY_ROOT_ID))),
    [collapsibleIds],
  );

  const runExport = useCallback(async (exporter) => {
    setExporting(true);
    try {
      await exporter(captureRef.current, 'employees-org-chart');
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  }, []);

  // Auto-fit after the final layout for the current search/filter criteria is
  // stable. Search opens ancestor paths in a follow-up render, so debounce on
  // width/height; the last layout wins and matches the toolbar's Fit to screen.
  const fitCriteriaKey = useMemo(() => JSON.stringify({
    query: query.trim(),
    siteIds: filters.siteIds,
    departmentIds: filters.departmentIds,
    status: filters.status,
    employmentStatus,
  }), [query, filters, employmentStatus]);
  const fittedCriteriaRef = useRef(null);

  useEffect(() => {
    if (
      !initialLayoutReady ||
      loading ||
      loadedStatus !== employmentStatus ||
      !employees.length ||
      !width ||
      !height ||
      fittedCriteriaRef.current === fitCriteriaKey
    ) return undefined;

    const timer = window.setTimeout(() => {
      fitToScreen();
      fittedCriteriaRef.current = fitCriteriaKey;
    }, 100);

    return () => window.clearTimeout(timer);
  }, [
    initialLayoutReady, loading, loadedStatus, employmentStatus, employees.length,
    width, height, fitCriteriaKey, fitToScreen,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName);

      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (event.key === 'Escape' && typing) {
        setQuery('');
        searchRef.current?.blur();
        return;
      }
      if (typing) return;

      if (event.key === '+' || event.key === '=') zoomIn();
      if (event.key === '-' || event.key === '_') zoomOut();
      if (event.key === '0') fitToScreen();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoomIn, zoomOut, fitToScreen]);

  const siteGroups = useMemo(
    () => [{ key: 'sites', label: 'Sites', items: siteData.map((s) => ({ value: s.id, label: s.name })) }],
    [siteData],
  );

  // Same grouping as the employee list: department names repeat across sites, so
  // a flat list would show several identical rows with no way to tell them apart.
  const departmentGroups = useMemo(() => {
    const bySite = departmentData.reduce((acc, d) => {
      const site = d.site_name || 'No site';
      (acc[site] ||= []).push({ value: d.id, label: d.name });
      return acc;
    }, {});

    return Object.entries(bySite)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([site, items]) => ({ key: site, label: site, items: items.sort((x, y) => x.label.localeCompare(y.label)) }));
  }, [departmentData]);

  const selectedNode = selectedId ? byId.get(selectedId) : null;
  const selectedManager = useMemo(() => {
    if (!selectedNode) return null;
    const managerId = parentById.get(selectedNode.id);
    return managerId ? byId.get(managerId) : null;
  }, [selectedNode, parentById, byId]);

  const selectedReports = useMemo(() => {
    if (!selectedNode) return [];
    return (childrenByParent.get(selectedNode.id) || []).map((id) => byId.get(id)).filter(Boolean);
  }, [selectedNode, childrenByParent, byId]);

  const anomalyCount = anomalies.orphanedParents + anomalies.brokenCycles;

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-flex tw-items-center tw-gap-3 tw-py-4">
        <button
          type="button"
          onClick={() => navigate('/organization/employees')}
          title="Back to the employee list"
          className="tw-inline-flex tw-items-center tw-justify-center tw-h-9 tw-w-9 tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50"
        >
          <MdArrowBack size={18} />
        </button>
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-10 tw-w-10 tw-rounded-lg tw-bg-primary-50 tw-text-primary-500">
            <FaSitemap size={18} />
          </span>
          <div>
            <h4 className="tw-text-lg tw-font-semibold tw-text-slate-800 tw-m-0">Org chart</h4>
            <p className="tw-text-xs tw-text-slate-400 tw-m-0">
              {employees.length}
              {employmentStatus === 'active' && ' active'}
              {employmentStatus === 'inactive' && ' deactivated'}
              {' employees · reporting lines by manager'}
            </p>
          </div>
        </div>
      </div>

      {anomalyCount > 0 && (
        <div className="tw-flex tw-items-start tw-gap-2.5 tw-mb-3 tw-px-4 tw-py-2.5 tw-rounded tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200">
          <MdWarningAmber size={17} className="tw-text-amber-500 tw-shrink-0 tw-mt-0.5" />
          {/* Two different problems, said separately. A missing manager is usually
              just a deactivated one — normal, and fixed by assigning a new
              manager. A loop is genuinely corrupt data. Calling both "corruption"
              would send people looking for a bug that is not there. */}
          <p className="tw-text-xs tw-text-amber-800 tw-m-0">
            {anomalies.orphanedParents > 0 && (
              <>
                <strong>{anomalies.orphanedParents}</strong> employee(s) report to someone
                who is not in this view — they are shown at the top level.
                {employmentStatus === 'active'
                  ? ' Switch the status filter to "Active + deactivated" to see whether their manager simply left.'
                  : ' Their manager row no longer exists at all.'}{' '}
              </>
            )}
            {anomalies.brokenCycles > 0 && (
              <>
                <strong>{anomalies.brokenCycles}</strong> reporting loop(s) were found and
                cut for display; that is corrupt data — run{' '}
                <code className="tw-font-mono">rake organization:ancestry:repair</code>.
              </>
            )}
          </p>
        </div>
      )}

      {loading && !employees.length ? (
        <div className="tw-text-center tw-py-16"><LoadingSpinner /></div>
      ) : employees.length === 0 ? (
        <EmptyState
          icon={FaSitemap}
          title="No employees to chart"
          description="Nobody active was returned for the organization chart."
        />
      ) : (
        <>
          <OrgChartToolbar
            query={query}
            onQueryChange={setQuery}
            matchCount={matchedList.length}
            matchIndex={matchIndex}
            onPrevMatch={() => goToMatch(matchIndex - 1)}
            onNextMatch={() => goToMatch(matchIndex + 1)}
            siteGroups={siteGroups}
            departmentGroups={departmentGroups}
            filters={filters}
            onFiltersChange={setFilters}
            employmentStatus={employmentStatus}
            onEmploymentStatusChange={setEmploymentStatus}
            loading={loading}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onFit={fitToScreen}
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            onExportPng={() => runExport(exportChartPng)}
            onExportPdf={() => runExport(exportChartPdf)}
            exporting={exporting}
            searchRef={searchRef}
          />

          <div className="tw-flex tw-gap-3 tw-items-stretch">
            <div className="tw-flex-1 tw-min-w-0">
              <OrgChartCanvas
                ref={viewportRef}
                captureRef={captureRef}
                nodes={nodes}
                edges={edges}
                width={width}
                height={height}
                transform={transform}
                isPanning={isPanning}
                panHandlers={panHandlers}
                focusedId={selectedId ?? focusedId}
                onToggle={toggleNode}
                onFocus={(node) => {
                  // A drag that ended without a drop must not also select.
                  if (consumedClick()) return;
                  selectNode(node.id);
                }}
                onChangeManager={(node) => setManagerTargets({ people: [node], suggested: null })}
                onEdit={setEditing}
                wasDragged={wasDragged}
                dragHandlersFor={dragHandlersFor}
                draggingId={dragging?.id}
                dropTargetId={dropTargetId}
              />
            </div>

            {selectedNode && (
              <OrgChartDetailPanel
                node={selectedNode}
                manager={selectedManager}
                reports={selectedReports}
                onClose={() => setSelectedId(null)}
                onChangeManager={() => setManagerTargets({ people: [selectedNode], suggested: null })}
                onMoveReports={() => setManagerTargets({
                  people: selectedReports,
                  directReportsOf: selectedNode,
                })}
                onEdit={() => setEditing(selectedNode)}
                onSelect={selectNode}
              />
            )}
          </div>

          <OrgChartUnassigned people={unassigned} inactiveCount={inactiveUnassigned} />
        </>
      )}

      {/* Follows the cursor during a drag. Fixed-position and outside the scaled
          canvas, so it stays a constant, readable size at any zoom. */}
      {dragging && ghostPosition && (
        <div
          className="tw-fixed tw-pointer-events-none tw-z-[100003] tw-px-2.5 tw-py-1.5 tw-rounded tw-bg-slate-800 tw-text-white tw-text-xs tw-font-semibold tw-shadow-lg"
          style={{ left: ghostPosition.x + 12, top: ghostPosition.y + 12 }}
        >
          {dragging.full_name || dragging.name}
          <span className="tw-block tw-text-[10px] tw-font-normal tw-text-slate-300">
            {dropTargetId ? `Drop on ${byId.get(dropTargetId)?.name} to reassign` : 'Drop on a manager'}
          </span>
        </div>
      )}

      {managerTargets && (
        <ChangeManagerForm
          open
          setOpen={() => setManagerTargets(null)}
          records={managerTargets.people}
          directReportsOf={managerTargets.directReportsOf}
          onSuccess={refetchChart}
        />
      )}

      {editing && (
        <OrgChartEditDrawer
          employeeId={editing.id}
          employeeName={editing.full_name || editing.name}
          onClose={() => setEditing(null)}
          onSaved={refetchChart}
        />
      )}
    </div>
  );
};

export default EmployeeOrgChartPage;
